import { useCallback, useEffect, useRef, useState } from 'react';
import { fetchTakeoverScript, recordZanaReceipt } from '../services/briefingsApi';
import type { AskPageContext } from '../services/briefingsApi';
import {
  runTakeover, clearSpotlight,
  TakeoverController, TakeoverRequest, TakeoverState,
} from '../services/zanaTakeover';
import { ZanaIntentExecutor } from '../services/zanaActions';
import { BRANCH_SWITCHED_REPLY } from '../services/zanaAskShared';
import {
  episodeToChatText, newAgentId, selfNameForTopic,
  type ZanaEpisode,
} from '../services/zanaEpisodes';
import {
  registerFgAgent, releaseFgAgent, markFgDone, updateFgAgent,
  tryAcquireUiLock, FG_MAX,
} from '../services/zanaAgentRegistry';

/** Known walkthroughs → open their landing screen before the script fetch
 *  returns. Ask Zana prints "On it…" first; without this, owners sat on that
 *  tab for the round-trip (~seconds) with nothing moving.
 *
 *  MUST match lens/script open.tab (topic of concern). Dashboard is only for a
 *  true fleet tour — never fuel/routes/drivers soft-opens. */
const PREOPEN_TAB: Record<string, string> = {
  invite_driver: 'accounts',
  ground_bus_docs: 'maintenance',
  maintenance: 'maintenance',
  ops_delete_bus: 'fleet',
  ops_create_bus: 'fleet',
  ops_suspend_bus: 'fleet',
  ops_suspend_driver: 'accounts',
  clear_alerts: 'highRisk',
  alerts: 'highRisk',
  fuel_leak: 'fuelReport',
  missing_refuels: 'fuelReport',
  fuel: 'fuelReport',
  money: 'fuelReport',
  fuelPrices: 'fuelPrices',
  route_slipping: 'analytics',
  routes: 'analytics',
  driver_coaching: 'analytics',
  drivers: 'analytics',
  accounts: 'accounts',
  trips: 'trips',
  liveMap: 'liveMap',
  monthlyReport: 'monthlyReport',
  fleet: 'dashboard',
};

/** Mirrors the runner's START_GRACE_MS: a click this soon after the start is
 *  the tail of the click that started her, not a hand on the wheel. */
const STUB_NUDGE_GRACE_MS = 1600;

function topicFromReq(req: TakeoverRequest): string {
  if ('topic' in req && req.topic) return String(req.topic);
  if ('scriptId' in req && req.scriptId) return String(req.scriptId);
  return '';
}

/**
 * Owns one takeover at a time.
 *
 * It lives above the dock on purpose. The dock is the conversation (Ask +
 * interrupt); the panel is the steering wheel. Both stay reachable: Copilot
 * auto-opens when a walk starts so "stop" is one word away, and the panel
 * drives tabs/overlays on the subject screen.
 *
 * Phase 2+: named FG agent chrome + registry slots (max 3).
 */
export function useZanaTakeover({
  orgCode,
  onTab,
  onExecuteIntent,
  onSummary,
  pageContext,
  liveCounts,
}: {
  orgCode: string | null;
  /** Switch the console to a tab. Same allow-list as an open_tab intent.
   *  `spotlight` lets the console pick the right sub-tab within it. */
  onTab: (tab: string, spotlight?: string[]) => void;
  /** Runs the confirmed writes. Returns a receipt line she reads out. */
  onExecuteIntent?: ZanaIntentExecutor;
  /** Posts her closing report into the chat after an unattended run. */
  onSummary?: (text: string) => void;
  /** Screen the owner is on — cards/focus bend the composed walk toward it. */
  pageContext?: AskPageContext | null;
  /** Live High Risk / unread from the open dashboard — same as Ask.
   *  `missingRefuelCount` is here so the walkthrough narrates the number the
   *  WRITE will act on; without it she announced a cached figure and then
   *  flagged from the console's own, which is how she came to promise buses she
   *  could not flag. */
  liveCounts?: {
    unreadAlertCount?: number;
    highRiskBusCount?: number;
    unreadBusIds?: string[];
    missingRefuelCount?: number;
  } | null;
}) {
  const [state, setState] = useState<TakeoverState | null>(null);
  /** Why she could not start, for the caller to show wherever it makes sense. */
  /* One channel into the chat, for anything she needs to SAY outside the
     panel: a refusal to start, or the closing report after an unattended run.
     It was called `refusal` when that was all it carried — a success summary
     posted through something named refusal is the kind of naming that misleads
     the next reader. */
  const [notice, setNotice] = useState<string | null>(null);
  /** Structured episode attached to the current notice (Ask payload v2). */
  const [noticeEpisode, setNoticeEpisode] = useState<ZanaEpisode | null>(null);
  const controllerRef = useRef<TakeoverController | null>(null);
  /* 2026-09-17: set when the owner stopped her by typing in chat ("stop").
     The chat surface already said "I'm back in chat" as it handled the word,
     so onEnd must not post the same line again ~580ms later. Reset per run. */
  const stoppedFromChatRef = useRef(false);
  const agentIdRef = useRef<string | null>(null);
  const agentStartedAtRef = useRef(0);
  const agentMetaRef = useRef<{ name: string; topic: string; goal: string; mode: 'walkthrough' | 'agent' }>({
    name: 'Desk Agent', topic: '', goal: '', mode: 'walkthrough',
  });
  /** Latest callbacks, so a parent re-render never restarts a live run. */
  const cbs = useRef({ onTab, onExecuteIntent, onSummary, pageContext, liveCounts });
  cbs.current = { onTab, onExecuteIntent, onSummary, pageContext, liveCounts };
  /* 2026-09-17: the org open NOW. The console stays mounted across a branch
     switch, and `cbs.current.onExecuteIntent` acts on whichever branch is open
     when it runs — so a script composed for branch A, landing or holding its
     Yes after a switch to B, drove B's tabs and ran A's writes against B. A run
     compares this with the org it started in before it goes on. */
  const orgRef = useRef(orgCode);
  orgRef.current = orgCode;

  // A takeover must not outlive the screen it is driving.
  useEffect(() => () => {
    controllerRef.current?.stop();
    controllerRef.current = null;
    clearSpotlight();
    if (agentIdRef.current) {
      releaseFgAgent(agentIdRef.current);
      agentIdRef.current = null;
    }
  }, []);

  /* GO STRAIGHT THERE (Gabriel, 2026-09-18). No panel, no runner, no rail:
     open the subject's screen, glow the subject, hand one line to the chat.
     The engine picks the destination (composeJump) with the same lens rules the
     walk's opener uses, so "show me KCE999Z" and "walk me through KCE999Z" land
     in the same place — one of them just stops there.
     The pre-open below means the tab moves on the click, not after the fetch;
     if the fetch fails the owner is still on the right screen. */
  const jump = useCallback(async (req: { topic: string; ops?: TakeoverRequest['ops']; findingTitle?: string; findingKey?: string }) => {
    if (!orgCode) return;
    const askedIn = orgCode;
    /* An incident finding lands on the bus's own Incidents tab (composeJump,
       2026-09-28). Pre-opening its category queue first flashed High Risk for
       a beat and then swapped it out — "it took me to alerts before reaching
       the incidents" (Gabriel, 2026-09-29). Same test as the server's. */
    const isIncident = /incident/i.test(req.findingKey || '') || /incident/i.test(req.findingTitle || '');
    const pre = isIncident ? '' : (PREOPEN_TAB[req.topic] || '');
    if (pre) cbs.current.onTab(pre);
    const res = await fetchTakeoverScript(orgCode, {
      topic: req.topic,
      mode: 'jump',
      ...(req.ops ? { ops: req.ops } : {}),
      ...(req.findingTitle ? { findingTitle: req.findingTitle } : {}),
      ...(req.findingKey ? { findingKey: req.findingKey } : {}),
      ...(cbs.current.pageContext ? { pageContext: cbs.current.pageContext } : {}),
      ...(cbs.current.liveCounts ? { liveCounts: cbs.current.liveCounts } : {}),
    });
    // A branch switch while it loaded: the destination belongs to the old one.
    if (orgRef.current !== askedIn) return;
    const landing = res.ok && res.script && res.script.steps && res.script.steps[0];
    if (!landing) return;
    // One bus, no better screen: open the bus itself. The console's open_bus
    // picks the vehicle, opens its detail and moves the tab (2026-09-20) —
    // landing on the desk overview was exactly the complaint.
    const openBus = res.script && res.script.openBus;
    // `openBusTab` (2026-09-28): an incident finding opens the bus ON its
    // Incidents tab — the list the finding is about — instead of High Risk.
    const openBusTab = res.script && res.script.openBusTab;
    if (openBus) cbs.current.onExecuteIntent?.({ actionId: 'open_bus', params: { busId: openBus, ...(openBusTab ? { tab: openBusTab } : {}) } });
    else if (landing.tab) cbs.current.onTab(landing.tab, landing.spotlight || []);
    else if ((landing.spotlight || []).length) cbs.current.onTab(pre || 'dashboard', landing.spotlight);
    // The line goes through `notice`, which the console already pipes into the
    // dock's thread (and into the branch's stored thread when the dock is
    // closed). `onSummary` is an unattended run's closing report and is not
    // wired on the console, so a landing line sent there would vanish.
    if (landing.say) {
      setNoticeEpisode(null);
      setNotice(landing.say);
    }
  }, [orgCode]);

  const start = useCallback(async (req: TakeoverRequest) => {
    if (!orgCode || controllerRef.current) return;
    setNotice(null);
    setNoticeEpisode(null);
    stoppedFromChatRef.current = false;

    const topic = topicFromReq(req);
    const kind = (req as { kind?: 'show' | 'do' }).kind
      || ((req as { walkthrough?: boolean }).walkthrough ? 'show' : undefined);
    /* Guide "Show me" / kind=show → teach (walkthrough). kind=do / act → FG agent.
       Default: guided walk is walkthrough; auto/skip with act intent is agent. */
    const mode: 'walkthrough' | 'agent' = kind === 'do' || req.auto === true
      ? 'agent'
      : 'walkthrough';
    const agentName = selfNameForTopic(topic);
    const agentId = newAgentId();
    const goal = String((req as { findingTitle?: string }).findingTitle || topic || 'Desk walk');

    const reg = registerFgAgent({
      agentId,
      name: agentName,
      topic,
      goal,
      mode,
      estimateMs: 90_000,
      taskTitle: mode === 'agent' ? 'Queued' : 'Walkthrough',
    });
    if (!reg.ok) {
      setNoticeEpisode(null);
      setNotice(reg.reason);
      return;
    }
    agentIdRef.current = agentId;
    agentStartedAtRef.current = Date.now();
    agentMetaRef.current = { name: agentName, topic, goal, mode };
    tryAcquireUiLock(agentId);

    if (mode === 'agent') {
      setNoticeEpisode(null);
      setNotice(`I'm splitting into mini-Zanas to work on this — Zana · ${agentName}. You'll be notified when they're done.`);
    }

    // Open the destination immediately — do not wait on the compose/fetch.
    const scriptId = 'scriptId' in req ? req.scriptId : '';
    const pre = scriptId ? PREOPEN_TAB[scriptId] : (topic ? PREOPEN_TAB[topic] : '');
    if (pre) cbs.current.onTab(pre);
    else if (topic === 'alerts') cbs.current.onTab('highRisk');
    else if (topic === 'fuel' || topic === 'money') cbs.current.onTab('fuelReport');
    else if (topic === 'fuelPrices') cbs.current.onTab('fuelPrices');
    else if (topic === 'routes' || topic === 'drivers') cbs.current.onTab('analytics');
    else if (topic === 'ground_bus_docs' || topic === 'maintenance') cbs.current.onTab('maintenance');

    const withPage = {
      ...req,
      ...(cbs.current.pageContext ? { pageContext: cbs.current.pageContext } : {}),
      ...(cbs.current.liveCounts ? { liveCounts: cbs.current.liveCounts } : {}),
    };

    /* ASK WHILE IT LOADS.
     *
     * Composing a script is a round trip to the engine — about three seconds,
     * which Gabriel felt immediately (2026-08-03: "it takes too long, it took
     * like three seconds after clicking"). The question does not depend on a
     * single line of that script, so asking it afterwards spent the wait twice:
     * once staring at nothing, then again reading the question.
     *
     * The choice panel goes up on the click and the fetch runs behind it. By
     * the time anyone has read two options and picked one, the script is
     * usually here — dead waiting turned into decision time. */
    const skipChooser = !!req.skipChooser;
    let choose: (auto: boolean) => void = () => {};
    /* 2026-09-17: skipping the question is not the same as answering "just do
       it". The server's autoRun means "the owner already consented" and is set
       for navigate / show_me asks as well as act asks, and Guide Mode's "Show
       me" promises "I'll walk you through…" — so a skipped chooser runs the
       guided walk unless the caller explicitly asked for an unattended run. */
    const chosen = skipChooser
      ? Promise.resolve(req.auto === true)
      : new Promise<boolean>((resolve) => { choose = resolve; });
    let abandoned = false;
    /* A stub controller so Stop works before the runner exists — in BOTH
       paths. 2026-09-17: the skip-chooser path used to leave this null for the
       whole script fetch, so the panel rendered nothing, the dock's "stop" hit
       `controller?.stop()` on null, and the one-run guard above let a second
       start through: two runners fighting over the tabs, the first orphaned. */
    const stubAt = Date.now();
    const stub: TakeoverController = {
      choose: (auto: boolean) => choose(auto),
      stop: () => {
        abandoned = true;
        controllerRef.current = null;
        setState(null);
        if (agentIdRef.current) {
          releaseFgAgent(agentIdRef.current);
          agentIdRef.current = null;
        }
      },
      pause: () => {}, resume: () => {}, carryOn: () => {},
      answerConfirm: () => {}, next: () => {}, back: () => {},
      /* 2026-09-17: a click on the page while she is still loading ends the
         start, as it would a running walk (the runner's nudge yields to chat).
         Once the chooser is answered — or skipped — the panel sits in
         `driving` for the whole compose, click guard up and "click anywhere to
         pause" on screen, and a no-op here left that promise dead for seconds.
         Same start grace as the runner, so a double-click on the button that
         started her does not cancel her. Never during `choosing`: the guard
         is down there, and the chooser has its own way out. */
      nudge: () => {
        if (Date.now() - stubAt < STUB_NUDGE_GRACE_MS) return;
        if (controllerRef.current === stub) stub.stop();
      },
    };
    controllerRef.current = stub;
    /** The owner switched branches while this was loading: end the start and
     *  say so in chat, rather than walk the new branch with the old one's script. */
    const leftBranch = () => {
      if (orgRef.current === orgCode) return false;
      abandoned = true;
      if (controllerRef.current === stub) controllerRef.current = null;
      setState(null);
      if (agentIdRef.current) {
        releaseFgAgent(agentIdRef.current);
        agentIdRef.current = null;
      }
      setNoticeEpisode(null);
      setNotice(BRANCH_SWITCHED_REPLY);
      return true;
    };
    const bannerTitle = `Zana · ${agentName}`;
    if (!skipChooser) {
      setState({
        title: bannerTitle,
        phase: 'choosing',
        say: 'Want me to work through this myself and tell you what I did — or walk you through it?',
        face: 'talking',
        step: 0,
        total: 0,
        countdown: 0,
        confirm: null,
        read: null,
        readLeft: 0,
        readTotal: 0,
        suggest: null,
        leaving: false,
        agentName,
        agentMode: mode,
        taskTitle: 'How should I work this?',
        agentStatus: 'waiting_you',
      });
    } else {
      setState({
        title: bannerTitle,
        phase: 'driving',
        say: 'On it — opening it up…',
        face: 'thinking',
        step: 0,
        total: 0,
        countdown: 0,
        confirm: null,
        read: null,
        readLeft: 0,
        readTotal: 0,
        suggest: null,
        leaving: false,
        agentName,
        agentMode: mode,
        taskTitle: 'Opening…',
        agentStatus: 'working',
      });
      updateFgAgent(agentId, { status: 'working', taskTitle: 'Opening…' });
    }

    const scriptP = fetchTakeoverScript(orgCode, withPage);
    const auto = await chosen;
    if (abandoned || leftBranch()) return;

    /* Say something while the rest of the compose lands.
     *
     * Parallelising the fetch removed the wait BEFORE the question; it could
     * not remove the wait after it, because someone who knows what they want
     * answers in half a second. Gabriel then sat on a frozen panel — which
     * reads as broken, not busy (2026-08-03, bug 59).
     *
     * The wait itself is usually the org context being rebuilt: it is cached
     * for ten minutes, so the first takeover after a quiet spell pays for the
     * rebuild and the ones after it are quick. Nothing here makes that faster.
     * It just stops pretending nothing is happening. */
    setState((prev) => (prev ? {
      ...prev,
      phase: 'driving',
      say: auto ? 'On it — getting your fleet up…' : 'One moment — opening it up…',
      face: 'thinking',
      taskTitle: auto ? 'Working…' : 'Opening…',
      agentStatus: 'working',
    } : prev));
    updateFgAgent(agentId, { status: 'working', taskTitle: auto ? 'Working…' : 'Opening…' });

    const res = await scriptP;
    if (abandoned || leftBranch()) return;
    if (!res.ok || !res.script || !res.script.steps?.length) {
      controllerRef.current = null;
      setState(null);
      if (agentIdRef.current) {
        releaseFgAgent(agentIdRef.current);
        agentIdRef.current = null;
      }
      // Refusing is the correct outcome when the data is thin — say why rather
      // than driving a walkthrough with gaps in it.
      setNoticeEpisode(null);
      setNotice(
        res.reason === 'insufficient_data'
          ? "I don't have enough on that yet to walk you through it honestly — the figures it needs aren't in your fleet's data."
          : "I couldn't start that walkthrough just now. Try me again in a moment.",
      );
      return;
    }

    const scriptTitle = res.script.title || agentName;
    controllerRef.current = runTakeover({
      ...res.script,
      title: bannerTitle,
    }, {
      onTab: (tab, spotlight) => {
        if (agentIdRef.current && !tryAcquireUiLock(agentIdRef.current)) {
          updateFgAgent(agentId, { status: 'queued', taskTitle: 'Waiting for screen…' });
          return;
        }
        updateFgAgent(agentId, { status: 'working', taskTitle: `Opening ${tab}` });
        cbs.current.onTab(tab, spotlight);
      },
      onState: (s) => {
        const taskTitle = s.phase === 'holding' ? 'Waiting on you — confirm'
          : s.phase === 'waiting' || s.phase === 'reading' ? (s.say || 'Waiting on you').slice(0, 80)
            : s.phase === 'paused' ? 'Paused'
              : s.phase === 'done' ? 'Done'
                : (s.say || 'Working…').slice(0, 80);
        const agentStatus = s.phase === 'holding' || s.phase === 'waiting' || s.phase === 'reading' || s.phase === 'choosing'
          ? 'waiting_you' as const
          : s.phase === 'done' ? 'done' as const
            : 'working' as const;
        updateFgAgent(agentId, { status: agentStatus, taskTitle });
        setState({
          ...s,
          title: bannerTitle,
          agentName,
          agentMode: mode,
          taskTitle,
          agentStatus,
        });
      },
      onAct: async (actionId, params) => {
        // A walk still on screen after a branch switch holds A's Yes over B's
        // executor. Refused: the runner's failure line says nothing went
        // through and stops the walk.
        if (orgRef.current !== orgCode) throw new Error('branch switched during the walk');
        const result = await cbs.current.onExecuteIntent?.({
          actionId,
          params,
          confirmRequired: true,
        });
        return typeof result === 'string' ? result : undefined;
      },
      onSummary: (text, meta) => {
        const ep: ZanaEpisode = {
          type: 'agent_episode',
          agentId,
          name: agentName,
          kind: mode === 'agent' ? 'foreground' : 'walkthrough',
          goal,
          topic: topic || undefined,
          tabsVisited: meta.tabsVisited || [],
          tasksDone: meta.changed ? ['write.confirm'] : (meta.tabsVisited || []).map((t) => `ui.open_tab:${t}`),
          summary: text.split('\n')[0].slice(0, 400),
          startedAt: agentStartedAtRef.current || Date.now(),
          endedAt: Date.now(),
          report: {
            changed: meta.changed,
            lines: text.split('\n').filter(Boolean).slice(0, 12),
          },
        };
        setNoticeEpisode(ep);
        setNotice(episodeToChatText(ep) + (text.includes('\n') ? `\n${text}` : ''));
        /* 2026-09-17: receipts are recorded HERE and only here, and only when
           a write landed. The dock used to record every notice it showed —
           refusals and "I'm back in chat" included — and the backend keeps one
           receipt per org, so that junk overwrote the record of a real change. */
        if (orgCode && meta.changed) void recordZanaReceipt(orgCode, text);
        cbs.current.onSummary?.(text);
        markFgDone(agentId, ep);
      },
      onEnd: (reason) => {
        controllerRef.current = null;
        setState(null);
        if (agentIdRef.current) {
          if (reason === 'stopped') releaseFgAgent(agentIdRef.current);
          else markFgDone(agentIdRef.current);
          agentIdRef.current = null;
        }
        // Interrupt yield — leave a chat line so Ask is the obvious next step.
        // 2026-09-17: but never over a receipt. teardown('stopped') posts the
        // summary and ends in the same tick, and React batches the two, so a
        // plain set here replaced the only record of a write that went through.
        if (reason === 'stopped' && !stoppedFromChatRef.current) {
          setNotice((n) => n ?? "Okay — I'm back in chat. Ask me anything.");
        }
      },
    }, { auto });
    // Silence unused — kept for callers / future FG_MAX messaging.
    void scriptTitle;
    void FG_MAX;
  }, [orgCode]);

  /** Stop requested by a word typed in chat — the chat has already answered. */
  const stopFromChat = useCallback(() => {
    if (!controllerRef.current) return;
    stoppedFromChatRef.current = true;
    controllerRef.current.stop();
  }, []);

  const dismissNotice = useCallback(() => {
    setNotice(null);
    setNoticeEpisode(null);
  }, []);

  // Dock must stay usable once she pauses / waits / finishes a line. Hiding it
  // for the whole `state !== null` lifetime buried Ask Zana under the cockpit
  // and left owners with "no input" after a takeover. Only block while she is
  // actively moving the console or holding a write confirm / mode choice.
  const blockingPhase = !!state && (
    state.phase === 'driving'
    || state.phase === 'holding'
    || state.phase === 'choosing'
  );

  return {
    /** Non-null exactly while she is driving. */
    state,
    controller: controllerRef.current,
    driving: blockingPhase,
    /* 2026-09-17: a takeover is on screen, in ANY phase. `driving` is the
       narrow "hands off the dock" signal; a guided walk spends most of its
       life waiting / reading / paused, where "stop" must still stop it, the
       Guide check must stay quiet, and a typed yes must not run a write
       beside the panel's. Those read `active`. */
    active: !!state,
    /** Refusal to start, or her closing report — whatever she owes the chat. */
    notice,
    noticeEpisode,
    dismissNotice,
    start,
    /** Land on the thing and stop — what every direct ask does now. */
    jump,
    stopFromChat,
  };
}
